import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { getMaturityBand } from '../../lib/utils';

export const MaturityGauge = ({ maturity = 50, title = "Organizational Maturity" }) => {
  const band = getMaturityBand(maturity);
  
  // Calculate rotation angle for needle (-135 to 135 degrees)
  const angle = -135 + (maturity / 100) * 270;
  
  // SVG gauge parameters
  const cx = 100;
  const cy = 100;
  const radius = 70;
  
  // Arc path generator
  const describeArc = (startAngle, endAngle, r) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", start.x, start.y,
      "A", r, r, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
  };
  
  const polarToCartesian = (centerX, centerY, r, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180;
    return {
      x: centerX + r * Math.cos(angleInRadians),
      y: centerY + r * Math.sin(angleInRadians),
    };
  };

  return (
    <Card data-testid="maturity-gauge">
      <CardHeader>
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
        <p className="text-sm text-slate-500">
          Current learning maturity level and benchmark positioning
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center">
          <svg width="200" height="140" viewBox="0 0 200 140">
            {/* Background arc */}
            <path
              d={describeArc(-135, 135, radius)}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="16"
              strokeLinecap="round"
            />
            
            {/* Foundational segment (0-25%) - Red */}
            <path
              d={describeArc(-135, -67.5, radius)}
              fill="none"
              stroke="#fecaca"
              strokeWidth="16"
              strokeLinecap="round"
            />
            
            {/* Developing segment (25-50%) - Amber */}
            <path
              d={describeArc(-67.5, 0, radius)}
              fill="none"
              stroke="#fde68a"
              strokeWidth="16"
              strokeLinecap="butt"
            />
            
            {/* Acceleration segment (50-75%) - Green */}
            <path
              d={describeArc(0, 67.5, radius)}
              fill="none"
              stroke="#bbf7d0"
              strokeWidth="16"
              strokeLinecap="butt"
            />
            
            {/* Optimization segment (75-100%) - Teal */}
            <path
              d={describeArc(67.5, 135, radius)}
              fill="none"
              stroke="#99f6e4"
              strokeWidth="16"
              strokeLinecap="round"
            />
            
            {/* Needle */}
            <g transform={`rotate(${angle}, ${cx}, ${cy})`}>
              <line
                x1={cx}
                y1={cy}
                x2={cx}
                y2={cy - radius + 10}
                stroke="#1e3a5f"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <circle cx={cx} cy={cy} r="8" fill="#1e3a5f" />
              <circle cx={cx} cy={cy} r="4" fill="white" />
            </g>
            
            {/* Labels */}
            <text x="25" y="110" fontSize="10" fill="#64748b">0%</text>
            <text x="170" y="110" fontSize="10" fill="#64748b">100%</text>
          </svg>
          
          {/* Value display */}
          <div className="text-center mt-2">
            <p className="text-4xl font-heading font-bold text-slate-900">
              {maturity.toFixed(0)}%
            </p>
            <div
              className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
              style={{ backgroundColor: `${band.color}20`, color: band.color }}
            >
              {band.label}
            </div>
            <p className="mt-2 text-sm text-slate-500">{band.description}</p>
          </div>
        </div>
        
        {/* Maturity bands legend */}
        <div className="mt-6 grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-rose-200" />
            <span className="text-slate-600">0-25%: Foundational</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-200" />
            <span className="text-slate-600">25-50%: Developing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-200" />
            <span className="text-slate-600">50-75%: Acceleration</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-teal-200" />
            <span className="text-slate-600">75-100%: Optimization</span>
          </div>
        </div>
        
        {maturity > 75 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <strong>Note:</strong> Diminishing returns apply above 75% maturity. 
            Impact calculations are adjusted accordingly.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MaturityGauge;
